import React, { useState, useEffect } from "react";
import { Typography, Box, Stack, Button, useTheme } from "@mui/material";
import { IconPhoto, IconVideo, IconPhotoScan } from "@tabler/icons-react";
import colorsCustom from "../designTokens/colorsCustom";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

const CollectionContentOfTable = ({
	collection,
	selectedThread,
	setSelectedThread,
	isMobile,
	isVisible = false,
	setIsVisible,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [, setSearchParams] = useSearchParams();

	// 본문과 동일한 타이틀 생성 로직
	const getFirstSentence = (text) => {
		// 개행(\n)이나 마침표(.)로 첫 번째 문장 추출
		const sentences = text.split(/[\n.]/);
		return sentences[0].replace(/�/g, "").trim();
	};

	// 목차가 보일 때 본문 스크롤 중지
	useEffect(() => {
		if (isMobile && isVisible) {
			// body 스크롤 비활성화
			document.body.style.overflow = "hidden";
		} else {
			// body 스크롤 활성화
			document.body.style.overflow = "auto";
		}

		// cleanup 함수
		return () => {
			document.body.style.overflow = "auto";
		};
	}, [isMobile, isVisible]);

	return (
		<Stack
			spacing={1}
			sx={{
				width: isMobile ? "100%" : "256px", // 고정 너비로 변경 (40% → 300px)
				height: isMobile ? "100vh" : "calc(100vh - 88px - 24px)", // 헤더(64px + 24px gradient) - 24px를 고정 높이로 설정
				padding: isMobile ? 3 : "0 0 0 24px", // 좌측 패딩으로 변경
				position: isMobile ? "fixed" : "sticky", // 데스크톱에서 sticky
				top: isMobile ? 0 : "80px", // 헤더 + 16px 아래에 고정 (64px + 16px)
				left: isVisible ? 0 : "-100%",
				transition: "all 0.3s ease-in-out",
				zIndex: 8,
				backgroundColor: isMobile
					? theme.palette.background.default
					: "transparent", // 모바일에서만 툴바와 동일한 배경색
				borderLeft: isMobile
					? "none"
					: `1px solid ${colorsCustom.ui.dark.subtle}`, // 좌측 구분선으로 변경
				flexShrink: 0, // 크기 고정
				"& > :first-of-type": {
					position: "relative", // 첫 번째 자식(내부 Stack)에만 relative 적용
				},
			}}
		>
			<Stack
				sx={{
					position: "relative",
					height: "100%",
					overflow: "auto", // 스크롤 가능
					// 스크롤바 숨기기
					"&::-webkit-scrollbar": {
						display: "none",
					},
					"-ms-overflow-style": "none",
					"scrollbar-width": "none",
				}}
			>
				<Stack spacing={1} sx={{ paddingBottom: "80px" }}>
					<Typography
						variant="h6"
						sx={{ fontWeight: 400, color: colorsCustom.ui.dark.bold }}
					>
						{t("myCollection.tableOfContents")}
					</Typography>
					<Stack spacing={0}>
						{collection.threadList.map((thread, index) => (
							<Box
								key={index}
								sx={{
									padding: "16px 0",
									borderBottom:
										index === collection.threadList.length - 1
											? "none"
											: `1px solid ${colorsCustom.ui.dark.subtle}`,
									cursor: "pointer",
								}}
								onClick={() => {
									setSelectedThread(thread);
									setIsVisible(false);
									// URL 업데이트
									setSearchParams(
										(prev) => {
											const newParams = new URLSearchParams(prev);
											newParams.set("threadID", thread.id);
											return newParams;
										},
										{ replace: true }
									);
									// 스크롤을 최상단으로 이동
									window.scrollTo({ top: 0, behavior: "smooth" });
								}}
							>
								<Stack
									direction="row"
									spacing={2}
									alignItems="flex-start"
									sx={{ width: "100%" }}
								>
									{/* 넘버링 */}
									<Typography
										variant="body1"
										sx={{
											fontWeight: selectedThread === thread ? 800 : 600,
											color: colorsCustom.ui.dark.bold,
											minWidth: "24px",
											flexShrink: 0,
										}}
									>
										{index + 1}
									</Typography>

									{/* 타이틀과 날짜 */}
									<Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											variant="body2"
											sx={{
												fontWeight: selectedThread === thread ? 800 : 600,
												color: colorsCustom.ui.dark.bold,
												display: "block",
												lineHeight: "1.4",
												wordBreak: "break-word", // 긴 단어 줄바꿈
												whiteSpace: "normal", // 줄바꿈 허용
												hyphens: "auto", // 자동 하이픈 처리
											}}
										>
											{getFirstSentence(thread.text)}
										</Typography>

										{/* 날짜 표시 */}
										<Typography
											variant="caption"
											sx={{
												color: colorsCustom.ui.dark.moderate,
												fontSize: "0.75rem",
												lineHeight: 1.2,
											}}
										>
											{new Date(thread.timestamp).toLocaleDateString("ko-KR", {
												year: "numeric",
												month: "2-digit",
												day: "2-digit",
											})}
										</Typography>
									</Stack>

									{/* 미디어 아이콘 표시 */}
									{thread.hasMedia && (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												color: colorsCustom.ui.dark.moderate,
												flexShrink: 0,
											}}
										>
											{thread.isCarousel ? (
												<IconPhotoScan size={16} />
											) : thread.mediaType === "VIDEO" ? (
												<IconVideo size={16} />
											) : (
												<IconPhoto size={16} />
											)}
										</Box>
									)}
								</Stack>
							</Box>
						))}
					</Stack>
				</Stack>
			</Stack>

			{/* Gradient overlay at bottom */}
			<Box
				sx={{
					position: "absolute",
					bottom: 0,
					left: 0,
					right: 0,
					height: "80px",
					background: `linear-gradient(to top, ${
						isMobile
							? theme.palette.background.default
							: theme.palette.background.default
					} 0%, ${
						isMobile
							? theme.palette.background.default
							: theme.palette.background.default
					}00 100%)`,
					pointerEvents: "none",
					zIndex: 1,
				}}
			/>
		</Stack>
	);
};

export default CollectionContentOfTable;
